/**
 * Local structural nav config (Phase 4, R-401 / D1).
 *
 * Single ordered spine; each node carries a `markets: Market[]` filter so the
 * renderer derives per-market structure with `filterByMarket` — counts are
 * never hardcoded per market. The verified 7-vs-6 root-nav difference is the
 * `case-studies` node with `markets: ["US", "MX"]`.
 *
 * Node `id`s are the join keys (D2): display strings resolve through
 * `OBELIX_NAV_KEYS[id]` → ALH (`ads-nav`), while slugs/urls/images stay here as
 * structure. Seeded from the Contentful staging snapshot committed at
 * `tests/parity/fixtures/nav.json`; `tests/parity/configDrift.test.ts` holds
 * the config to it.
 */
export type Market =
  | "US"
  | "GB"
  | "AU"
  | "CA"
  | "BR"
  | "DE"
  | "ES"
  | "FR"
  | "IT"
  | "JP"
  | "KR"
  | "MX";

/** All site markets, in the order the nav/footer content models list them. */
export const ALL_MARKETS: readonly Market[] = [
  "US",
  "GB",
  "AU",
  "CA",
  "BR",
  "DE",
  "ES",
  "FR",
  "IT",
  "JP",
  "KR",
  "MX",
];

/** Contentful asset reference (A3 — media stays in the CMS; config names it). */
export type AssetRef = string;

export interface NavPageNode {
  id: string;
  kind: "page";
  slug: string;
  image?: AssetRef;
  markets: readonly Market[];
}

export interface NavFolderNode {
  id: string;
  kind: "folder";
  markets: readonly Market[];
  children: readonly NavPageNode[];
}

export type NavNode = NavPageNode | NavFolderNode;

export interface NavConfigShape {
  logo: NavPageNode;
  menu: readonly NavNode[];
  cta: NavPageNode;
}

/**
 * The nav spine, seeded from the verified Contentful staging data
 * (Root Navigation `dthUF0kNBiz8MXI5T2802` + the Solutions folder
 * `7pf1WvhbwQJHvI0V6T2wbW`). Ordering here IS the render order.
 */
export const navConfig = {
  logo: {
    id: "homepage",
    kind: "page",
    slug: "homepage",
    markets: ALL_MARKETS,
  },
  menu: [
    {
      id: "solutions",
      kind: "folder",
      markets: ALL_MARKETS,
      children: [
        {
          id: "ad-formats",
          kind: "page",
          slug: "ad-formats",
          image:
            "https://images.ctfassets.net/uc0h52j3d9rs/6u4knYQOVqreEk0dCwxluT/f3fdd0322c6325fd12ebb8b1a35dffdc/NavTile_EmilyInParis.png",
          markets: ALL_MARKETS,
        },
        {
          id: "targeting",
          kind: "page",
          slug: "targeting",
          image:
            "https://images.ctfassets.net/uc0h52j3d9rs/2K0VcC6IQjyCp2BXog56ZI/10e1f65f3179f677371adb4877966143/StrangerThings_S5_Nav.jpg",
          markets: ALL_MARKETS,
        },
        {
          id: "measurement-and-reporting",
          kind: "page",
          slug: "measurement-and-reporting",
          image:
            "https://images.ctfassets.net/uc0h52j3d9rs/1ZbA1yRcGju3nF3TdLb5cm/e5cef3200ff0a5be12dddc61f6577408/Full_Swing_n_S3_E7_00_42_14_09_Mobile.jpg",
          markets: ALL_MARKETS,
        },
      ],
    },
    { id: "insights", kind: "page", slug: "insights", markets: ALL_MARKETS },
    {
      id: "partnerships",
      kind: "page",
      slug: "partnerships",
      image:
        "https://images.ctfassets.net/uc0h52j3d9rs/4Y6w4Xgppx68TFP0d5g1Bj/97c61404d3552fcbc44fdc27fae76b03/resize-custom-partnerships-nav-image.png",
      markets: ALL_MARKETS,
    },
    // The 7-vs-6 root-nav difference: present only for the US and MX markets.
    { id: "case-studies", kind: "page", slug: "case-studies", markets: ["US", "MX"] },
    { id: "blog", kind: "page", slug: "blog", markets: ["US"] },
    { id: "faqs", kind: "page", slug: "faqs", markets: ALL_MARKETS },
  ],
  cta: {
    id: "get-connected",
    kind: "page",
    slug: "get-connected",
    markets: ALL_MARKETS,
  },
} as const satisfies NavConfigShape;

export type NavConfig = {
  logo: NavPageNode;
  menu: readonly NavNode[];
  cta: NavPageNode;
};

/**
 * Union of every node `id` in the spine — the join-key space shared with
 * `OBELIX_NAV_KEYS` (TypeScript rejects a config node without a registry
 * entry, and vice versa).
 */
export type NavNodeId =
  | NavIdsOf<typeof navConfig["logo"]>
  | NavIdsOf<(typeof navConfig["menu"])[number]>
  | NavIdsOf<typeof navConfig["cta"]>;

type NavIdsOf<T> = T extends { readonly id: infer I }
  ? T extends { readonly children: readonly unknown[] }
    ? I | NavIdsOf<T["children"][number]>
    : I
  : never;

/**
 * Derives the ordered node list for one market — counts and presence come from
 * the spine's `markets` filters, never from hardcoded per-market lists.
 */
export function filterByMarket(config: NavConfig, market: Market): NavConfig {
  const filterNode = (node: NavNode): NavNode | null => {
    if (!node.markets.includes(market)) return null;
    if (node.kind === "folder") {
      return {
        ...node,
        children: node.children.flatMap((child) => {
          const filtered = filterNode(child);
          return filtered ? [filtered] : [];
        }),
      } as NavFolderNode;
    }
    return node;
  };
  return {
    logo: config.logo,
    menu: config.menu.flatMap((node) => {
      const filtered = filterNode(node);
      return filtered ? [filtered] : [];
    }),
    cta: config.cta,
  };
}
